import { useEffect, useRef, useState } from "react";
import type { StatusSummary } from "@/lib/paging";

type Fetcher = (params: Record<string, unknown>) => Promise<StatusSummary>;

export function useStatusCounts(
  fetcher: Fetcher,
  groups: Record<string, string[]>,
  params: Record<string, string | undefined> = {},
): Record<string, number> | null {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const paramsKey = JSON.stringify(params);
  const groupsKey = JSON.stringify(groups);

  useEffect(() => {
    let cancelled = false;
    const tabs = groupsRef.current;

    fetcherRef
      .current({ ...paramsRef.current })
      .then((s) => {
        if (cancelled) return;
        const next: Record<string, number> = { all: s.total };
        for (const [tab, statuses] of Object.entries(tabs)) {
          next[tab] = statuses.reduce((sum, st) => sum + (s.byStatus[st] ?? 0), 0);
        }
        setCounts(next);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => { cancelled = true; };
  }, [paramsKey, groupsKey]);

  return counts;
}
